// clip-resolver.js — picks a Pexels mp4 URL from clip-library.json based on
// (brand, weather state, time-of-day bucket). Phase 1 ships with the 10
// verified clips from the design handoff; Phase 2 fills the remaining slots.
//
// Fallback chain per HANDOFF.md §6:
//   1. [brand][state][bucket]   brand-specific, exact bucket
//   2. [shared][state][bucket]  shared, exact bucket
//   3. [brand][state]['*']      brand-specific, any-time
//   4. [shared][state]['*']     shared, any-time
//   5. no clip                  the hero's plain dark backdrop
//
// A resolved clip is `{ src, still, credit }`: the proxied mp4, the clip's
// still photo (shown under the video and on its own when motion is
// withheld), and the Pexels credit for whoever shot it.

let LIBRARY = null;
let LOAD_PROMISE = null;

// Icon name → clip-library state key.
//
// The data bridge emits `clear-night` (WX_CODES has a night variant), but
// clip-library.json is keyed on `clear` and carries time-of-day in the BUCKET,
// not in the state. Before the suffix strip below, `clear-night` matched no
// state at all, so every clear night fell through the whole chain to the
// painted poster — the clip library's 8 fully-populated `clear` buckets were
// unreachable after dark. `drizzle` folds into `rain` the same way
// data-bridge.js's stateFromIcon does.
const aliasState = (s) => {
  const base = String(s || '').replace(/-night$/, '');
  if (base === 'partly-cloudy') return 'cloudy';
  if (base === 'drizzle') return 'rain';
  return base;
};

// Route any absolute (Pexels) clip URL through the same-origin /api/ext/video
// proxy so the browser never contacts videos.pexels.com directly. Mirrors the
// desktop proxyVideoUrl() in script.js. Relative/empty URLs pass through.
function proxyVideoUrl(raw) {
  const s = (raw || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return '/api/ext/video?url=' + encodeURIComponent(s);
  return s;
}

const LIBRARY_PATH = './m/clip-library.json';

// Width /api/ext/img downscales a server-picked clip's photo to. Those clips
// are landscape 720p/1080p video; 1280 matches the frame it sits under at
// roughly 100 KB (same value as the iOS app's HeroMediaService.stillWidth).
const STILL_WIDTH = 1280;

// Pexels serves a video's photo as `…?auto=compress&cs=tinysrgb&fit=crop
// &h=630&w=1200` — a 1.9:1 crop of a 16:9 frame. The hero fills with the
// video's own aspect, so that crop sat about 5 % zoomed against the clip.
// Dropping the crop parameters returns the whole frame; `w=` on the proxy
// then sizes it.
const PEXELS_CROP_KEYS = new Set(['fit', 'w', 'h', 'dpr', 'crop']);

function uncroppedPexelsImage(raw) {
  try {
    const u = new URL(raw);
    if (u.hostname.toLowerCase() !== 'images.pexels.com') return raw;
    for (const k of Array.from(u.searchParams.keys())) {
      if (PEXELS_CROP_KEYS.has(k.toLowerCase())) u.searchParams.delete(k);
    }
    return u.href;
  } catch (_) {
    return raw;
  }
}

// A still is either a static-library first frame (`hero-stills/<id>.jpg`,
// relative to clip-library.json, so it lands in /m/ under both fcc.cc/weather
// and weatherdog.net) or Pexels' photo of a server-picked clip, which goes
// through the image proxy so the browser never contacts images.pexels.com.
export function stillUrl(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) {
    return `/api/ext/img?url=${encodeURIComponent(uncroppedPexelsImage(s))}&w=${STILL_WIDTH}`;
  }
  try {
    return new URL(s, new URL(LIBRARY_PATH, document.baseURI)).href;
  } catch (_) {
    return '';
  }
}

export async function loadLibrary(versionTag = '') {
  if (LIBRARY) return LIBRARY;
  if (!LOAD_PROMISE) {
    const url = `${LIBRARY_PATH}${versionTag ? `?v=${encodeURIComponent(versionTag)}` : ''}`;
    // cache: no-cache — the library is served under the 7d /m/ expires but is
    // meant to be hot-editable; a conditional revalidation (304 when
    // unchanged) keeps clip swaps live without waiting out the cache.
    LOAD_PROMISE = fetch(url, { credentials: 'same-origin', cache: 'no-cache' })
      .then((r) => r.ok ? r.json() : {})
      .then((j) => { LIBRARY = j || {}; return LIBRARY; })
      .catch(() => { LIBRARY = {}; return LIBRARY; });
  }
  return LOAD_PROMISE;
}

const NO_CLIP = Object.freeze({ src: '', still: '', credit: null });

function clipFromSlot(slot) {
  if (!slot) return NO_CLIP;
  if (typeof slot === 'string') return { src: proxyVideoUrl(slot), still: '', credit: null };
  return {
    src: proxyVideoUrl(slot.url || ''),
    still: stillUrl(slot.poster),
    credit: { author: slot.author, title: slot.title, url: slot.pexels_url },
  };
}

/// The static library's answer: exact bucket, else the state's wildcard,
/// brand first, then shared.
export function resolveStaticClip(brandKey, state, bucket) {
  const lib = LIBRARY || {};
  const s = aliasState(state);
  const get = (brand) => {
    const st = lib[brand]?.[s];
    if (!st) return null;
    return st[bucket] || st['*'] || null;
  };
  return clipFromSlot(get(brandKey) || get('shared'));
}

// ---- lighting-aware fallback (2026-07-30) ---------------------------------
// clip-library.json only fills all 8 buckets for `clear`; every other state
// carries a single time-agnostic wildcard, and `fog` had no night clip at all
// — so a foggy midnight served a sunlit forest. When there is no exact bucket
// match, ask the server for a clip whose LIGHTING matches instead of dropping
// straight to that wildcard.
//
// Four tiers, not eight: Pexels distinguishes sunrise / daylight / golden hour
// / night, but "morning" and "afternoon" return the same footage. `pre-dawn`
// is night — it runs while it is still dark.
const LIGHTING_TIER = {
  'pre-dawn': 'night',
  night: 'night',
  sunrise: 'dawn',
  morning: 'day',
  midday: 'day',
  afternoon: 'day',
  'golden-hour': 'dusk',
  dusk: 'dusk',
};

// The server's scene vocabulary spells the overcast state `clouds`.
const SCENE_STATE = { cloudy: 'clouds' };

function exactSlot(brandKey, state, bucket) {
  const lib = LIBRARY || {};
  const s = aliasState(state);
  const get = (brand) => lib[brand]?.[s]?.[bucket] || null;
  return get(brandKey) || get('shared') || null;
}

const DYNAMIC_CACHE = new Map();

function fetchScene(scene) {
  if (DYNAMIC_CACHE.has(scene)) return DYNAMIC_CACHE.get(scene);
  const p = fetch(`/api/ext/pexels/hero-media?scene=${encodeURIComponent(scene)}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => j?.media || null)
    .catch(() => null);
  DYNAMIC_CACHE.set(scene, p);
  return p;
}

/// Resolves to `{ src, still, credit }`. Falls back to the static wildcard
/// whenever the dynamic lookup yields nothing, so an offline page keeps the
/// curated brand clips.
export async function resolveClipAsync(brandKey, state, bucket) {
  const exact = exactSlot(brandKey, state, bucket);
  if (exact) return clipFromSlot(exact);

  const tier = LIGHTING_TIER[bucket] || 'day';
  const s = aliasState(state);
  const media = await fetchScene(`${tier}-${SCENE_STATE[s] || s}`);
  if (media?.videoUrl) {
    return {
      src: proxyVideoUrl(media.videoUrl),
      still: stillUrl(media.posterUrl),
      // The server sends photographer / sourceUrl (api_ext_pexels_hero_media);
      // reading author / pexelsUrl alone left every server-picked clip
      // uncredited.
      credit: {
        author: media.photographer || media.author,
        title: media.title,
        url: media.sourceUrl || media.pexelsUrl,
      },
    };
  }

  return resolveStaticClip(brandKey, state, bucket);
}
